import cx from "classnames";
import { Fragment, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import { Button, EditableList, Text, Flex, ToolIcon, Tabs, Icon } from "@theorgchartdev/storybook";
import { ReRenderCanvas } from "@/Utils/StateLib";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import ViewMenu from "../../../Blocks/Menus/ViewMenu/ViewMenu";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QChartTemplateRule } from "@coreapp/CoreApp/QChartTemplateRule";
import {
  enumUISubModes,
  enumUIModes,
  enumMatchSubs,
  enumChartTemplateSet,
  enumFeaturesList,
  DISPLAY_MODES,
  SEARCH_PARAM_DISPLAY_KEY
} from "@coreapp/constants";
import { useRouteState } from "@/hooks/useRouteState";
import { isInViewManager } from "@coreapp/utils/isInViewManager";
import { Cappstr } from "@coreapp/utils/Cappstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { RuleRow } from "./RuleRow";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

export const BuildRuleOptions = (view, clickHandler, hoverHandler, editable) => {
  let options = [];

  const rulesList = view?.fRuleList ?? [];
  for (let ruleIdx = 0; ruleIdx < rulesList.length; ruleIdx++) {
    let rule = rulesList[ruleIdx];
    let ruleOption = {};
    ruleOption.content = <RuleRow rule={rule} index={ruleIdx + 1} />;
    ruleOption.value = ruleIdx + 1;
    ruleOption.hoverHandler = hoverHandler;
    if (editable) ruleOption.clickHandler = () => clickHandler(ruleOption, view);
    ruleOption.divider = true;
    options.push(ruleOption);
  }

  return options;
};

export const ViewTab = viewData => {
  //Given a singularView
  useWSMgrContext();
  const wsMgr = StateLib.GetWSMgr();
  let chartTempMgr = wsMgr.GetChartTemplatesMgr();
  const { setParam: setRouteParam } = useRouteState();

  const subMode = wsMgr.GetSubMode();
  const isInChartEditMode = Globals._uiConfig.GetUIMode() === enumUIModes.edit;
  // View-manager mode is URL-driven (?display=viewmanager) — shared helper
  // reads the URL so cold-start deep links and in-app clicks align.
  const isInViewEditorMode = isInViewManager();
  const activeView = viewData.view;
  const isChartRules = viewData.isChartRules;
  let canEverEdit =
    StateLib.GetUserInfo().CanUpdateTemplates() && getFeatureAccess(enumFeaturesList.editView);

  let curModeAllowsEdits = isChartRules ? isInChartEditMode : isInViewEditorMode && canEverEdit;

  let notEditingStr = !isChartRules
    ? appstr("Finish editing your chart to update these rules")
    : appstr("Finish editing your view to update these rules");

  const handleViewRuleEdit = (option, theView) => {
    const rule = theView.fRuleList[option.value - 1];
    chartTempMgr.handleRule({
      rule,
      index: option.value - 1,
      view: theView,
      onClose: HandleUpdatedRule
    });
  };

  const handleViewRulePreview = (option, hovering) => {
    const rule = activeView.fRuleList[option.value - 1];
    const w = StateLib.GetWSMgr();
    let targetDoc = StateLib.GetDoc();
    let canvas = targetDoc.fCanvasRef;
    canvas.ClearHighlightCache();
    if (rule) {
      const boxList = w.GetTargetTrees(false, targetDoc);
      const matchedBoxes = rule.GetMatchingBoxes(boxList, targetDoc);
      const hitBoxes = rule.GetRelatedBoxes(matchedBoxes, targetDoc);
      let numBoxes = hitBoxes.length;
      if (!hovering) {
        ReRenderCanvas();
        return;
      }
      for (let i = 0; i < numBoxes; i++) {
        canvas.DoHighlightElement(hitBoxes[i], true);
      }
    }
    ReRenderCanvas();
  };
  const createNewRule = theView => {
    const rule = new QChartTemplateRule();
    rule.InitChartTemplateRule(enumMatchSubs.matchonly, 0, false);
    chartTempMgr.handleRule({
      addNew: true,
      index: 0, //TODO: Fix index here.
      rule,
      view: theView,
      onClose: HandleUpdatedRule
    });

    return rule;
  };

  const clearRules = viewData => {
    viewData.view.fRuleList = [];
    HandleUpdatedRule(viewData);
  };

  const HandleUpdatedRule = viewData => {
    let wsMgr = StateLib.GetWSMgr();
    let doc = StateLib.GetDoc();
    let theView = viewData["view"];
    if (theView.GetTemplateLibrary() == enumChartTemplateSet.chartselfview) doc.SetDirty(true);
    else theView.SetDirty(true);
    let canvasPanel = wsMgr.fCanvasPanel;
    var didFormat = canvasPanel.AutoFormat();
    if (!didFormat) canvasPanel.RecalculateChartLayout(null);
    doc.ApplyChartGroupingToDoc();

    wsMgr.ReloadCurrentSubChart();
    StateLib.ReRenderCanvas();
  };

  const resetView = () => {
    let WSMgr = StateLib.GetWSMgr();
    let doc = StateLib.GetDoc();
    let activeView = doc.GetActiveTemplate();
    activeView.SetDirty(false);
    WSMgr.LoadChartTemplates(true);
  };

  const editView = () => {
    // URL drives view-manager sub-mode. TabularDisplayProvider's URL-sync
    // effect maps ?display=viewmanager → SwitchSubMode so coreapp fSubMode
    // stays aligned with the URL.
    setRouteParam(SEARCH_PARAM_DISPLAY_KEY, DISPLAY_MODES.VIEWMANAGER);
  };

  const finishViewEdit = () => {
    let WSMgr = StateLib.GetWSMgr();
    WSMgr.FinishActiveViewEdit();
  };

  const handleRuleUpdate = updater => {
    const nextRulesOptions = updater(
      BuildRuleOptions(activeView, handleViewRuleEdit, handleViewRulePreview, curModeAllowsEdits)
    );
    const newRulesList = nextRulesOptions.map(({ value }) => activeView.fRuleList[value - 1]);

    if (newRulesList.length === 0 && !isChartRules) {
      let alertMsg = appstr("lastrulemessage");
      wsMgr.CreateReactAlert(
        appstr("Unable to delete rule"),
        alertMsg,
        () => {},
        undefined,
        appstr("OK"),
        undefined
      );
    } else {
      activeView.fRuleList = newRulesList;
      HandleUpdatedRule({ view: activeView });
    }
  };

  return (
    <Fragment>
      {!isChartRules && (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          gap="4px"
          className={"sidebar-header-buttons"}
        >
          <Flex direction={"column"} width={"100%"} gap={"12px"}>
            <Text size={"small"} modifier={"bold"}>
              {appstr("Current View")}
            </Text>
            <Flex justifyContent={"space-between"} width={"100%"} padding={"0 4px"}>
              <ViewMenu />
              <Flex direction={"row"} gap="8px">
                {!curModeAllowsEdits ? (
                  <ToolIcon
                    name={"pen-to-square"}
                    tooltipProps={{ message: appstr("Edit View") }}
                    size="medium-small"
                    disabled={
                      !getFeatureAccess(enumFeaturesList.editView) ||
                      isInChartEditMode ||
                      activeView?.GetTemplateLibrary() === "system"
                    }
                    onClick={editView}
                    aria-label={"Edit view"}
                    dataTestId={chartModeTestId("sidebar", "view-rules-edit-btn")}
                  />
                ) : (
                  <Flex direction={"row"} gap="8px">
                    <ToolIcon
                      name={"check"}
                      tooltipProps={{ message: appstr("Save View") }}
                      size="medium-small"
                      disabled={activeView?.fRuleList.length === 0}
                      onClick={finishViewEdit}
                      aria-label={"Save view"}
                      dataTestId={chartModeTestId("sidebar", "view-rules-save-btn")}
                    />
                    <ToolIcon
                      name={"rotate-left"}
                      tooltipProps={{ message: appstr("Reset view") }}
                      size="medium-small"
                      disabled={!activeView?.IsDirty()}
                      onClick={resetView}
                      aria-label={"Reset view"}
                      dataTestId={chartModeTestId("sidebar", "view-rules-reset-btn")}
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      )}
      {!curModeAllowsEdits && isInChartEditMode && (
        <Flex alignItems="center" gap="4px" padding={"8px 16px"}>
          <Icon name="circle-exclamation" size="xsmall" modifier={"control"} />
          <Text size={"xsmall"} colorModifier={"lightgrey"}>
            {notEditingStr}
          </Text>
        </Flex>
      )}
      {curModeAllowsEdits && (
        <div className={cx("sidebar-header-buttons")}>
          <Button
            modifier={"secondary"}
            size={"small"}
            iconProps={{
              name: "plus-solid"
            }}
            disabled={!curModeAllowsEdits}
            dataTestId="rsb-view-add-rule"
            onClick={() => createNewRule(activeView)}
          >
            {appstr("Add rule")}
          </Button>
          {isChartRules && (
            <Button
              modifier={"secondary"}
              size={"small"}
              disabled={activeView?.fRuleList.length === 0 || !curModeAllowsEdits}
              iconProps={{
                name: "broom-wide"
              }}
              onClick={() => clearRules({ view: activeView })}
            >
              {appstr("Clear all")}
            </Button>
          )}
        </div>
      )}
      <div
        className={cx(
          "sidebar-content-container",
          "oc-overflow-auto",
          "oc-flex-1",
          "ocn-list-scrollbar"
        )}
      >
        <Flex direction={"column"}>
          <div className={cx("sidebar-content-item", "sidebar-content-item--divider")}>
            <Flex direction={"column"} gap={"4px"}>
              <Text size={"small"} colorModifier={"grey"}>
                {appstr("ruleorder")}
              </Text>
            </Flex>
          </div>
          {!activeView || activeView.fRuleList.length === 0 ? (
            curModeAllowsEdits ? (
              <Text size={"small"} colorModifier={"lightgrey"}>
                {appstr("Click add rule to start conditionally formatting your chart")}
              </Text>
            ) : (
              <Text size={"small"} colorModifier={"lightgrey"}>
                {appstr("No conditional formatting rules")}
              </Text>
            )
          ) : (
            <EditableList
              disableSort={!curModeAllowsEdits}
              disableRemove={!curModeAllowsEdits}
              listWidth="fill"
              options={BuildRuleOptions(
                activeView,
                handleViewRuleEdit,
                handleViewRulePreview,
                curModeAllowsEdits
              )}
              setOptions={handleRuleUpdate}
            />
          )}
        </Flex>
      </div>
    </Fragment>
  );
};

/**
 * @param viewDataArray a list of views to be displayed in this component, one per tab
 */
export const ViewDisplay = viewDataArray => {
  const viewCTX = useViewManagerContext();

  const [curTabIdx, setTabIdx] = useState(0);

  if (!viewCTX.viewMgr.state) return null;

  const views = viewDataArray.viewData;
  const GetTabs = () => {
    const options = [];
    for (const aViewObj of views) {
      const isChartRules = aViewObj.chartRule;
      const headerStr = isChartRules ? appstr("Rules from chart") : appstr("Rules from view");
      const option = {
        header: headerStr,
        dataTestId: isChartRules ? "view-tab-chart-rules" : "view-tab-view-rules"
      };
      option.content = (
        <ViewTab key={`view-tab-${headerStr}`} view={aViewObj.view} isChartRules={isChartRules} />
      );
      options.push(option);
    }
    return options;
  };

  if (views.length == 1) {
    return <ViewTab view={views[0].view} isChartRules={views[0].chartRule} />;
  }

  return (
    <Tabs
      tabSize={"medium"}
      tabIndex={curTabIdx}
      customClass={"ocn-sidebar-tabs"}
      customHeaderClass={"oc-tab-header-no-divider"}
      customContentClass={cx("ocn-chart-explorer-tabs-content", "oc-flex", "oc-flex-column")}
      tabs={GetTabs()}
      onTabChange={setTabIdx}
    />
  );
};
